/**
 * ContactTimeline.jsx
 *
 * Unified event feed for a single contact.
 * Merges: Outreach_Log, Demos, Trials — sorted newest first.
 *
 * Props:
 *   contact    — contact object (from Contacts sheet)
 *   data       — full sheet data from useSheetData
 *   getToken   — from useAuth (reserved for future deep-link fetches)
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchOutreachLog, saveContactNotes, refreshDealScore, saveNextAction, setContactPinned, fetchZoomNotesByContact, updateFirm } from '../api/backend.js';
import FirmCombobox from './FirmCombobox.jsx';
import { broadcastOutreachRefresh } from '../utils/dataRefresh.js';
import { computeStage, STAGE_LABELS, STAGE_BADGE_CLASS } from '../utils/stage.js';
import { computeHealth } from '../utils/health.js';
import { last6Months } from '../utils/amplitude.js';
import AmplitudeScoreChip from './AmplitudeScoreChip.jsx';
import UsageJournalPanel from './UsageJournalPanel.jsx';
import LumosityWatchlistCard from './LumosityWatchlistCard.jsx';
import { LUMOSITY_ENABLED } from '../api/lumosity.js';

const EVENT_ICONS = {
  email_outbound:  '📧',
  email_inbound:   '📩',
  bigdough:        '📬',
  demo_scheduled:  '📅',
  demo_completed:  '✅',
  demo_noshow:     '👻',
  trial_started:   '🧪',
  trial_active:    '🧪',
  trial_ended:     '📊',
  note:            '📝',
  zoom_meeting:    '🎥',
  default:         '🔵',
};

// Colors per source channel
const EVENT_COLORS = {
  bigdough:       { dot: '#3b82f6', badge: '#eff6ff', badgeText: '#1d4ed8', label: 'BigDough' },
  email_outbound: { dot: '#ef4444', badge: '#fef2f2', badgeText: '#b91c1c', label: 'Gmail' },
  email_inbound:  { dot: '#ef4444', badge: '#fef2f2', badgeText: '#b91c1c', label: 'Gmail' },
  demo_scheduled: { dot: '#10b981', badge: '#ecfdf5', badgeText: '#065f46', label: 'Calendar' },
  demo_completed: { dot: '#10b981', badge: '#ecfdf5', badgeText: '#065f46', label: 'Calendar' },
  demo_noshow:    { dot: '#f59e0b', badge: '#fffbeb', badgeText: '#92400e', label: 'Calendar' },
  trial_started:  { dot: '#8b5cf6', badge: '#f5f3ff', badgeText: '#5b21b6', label: 'Trial' },
  trial_active:   { dot: '#8b5cf6', badge: '#f5f3ff', badgeText: '#5b21b6', label: 'Trial' },
  trial_ended:    { dot: '#6b7280', badge: '#f9fafb', badgeText: '#374151', label: 'Trial' },
  zoom_meeting:   { dot: '#2563eb', badge: '#eff6ff', badgeText: '#1d4ed8', label: 'Zoom AI' },
  default:        { dot: '#94a3b8', badge: '#f8fafc', badgeText: '#475569', label: '' },
};

function eventColor(type) {
  return EVENT_COLORS[type] ?? EVENT_COLORS.default;
}

function eventIcon(type) {
  return EVENT_ICONS[type] ?? EVENT_ICONS.default;
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function daysAgo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const diff = Math.round((Date.now() - d.getTime()) / 86_400_000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 0) return `in ${Math.abs(diff)}d`;
  return `${diff}d ago`;
}

const TIER_COLOR = { healthy: '#16a34a', watch: '#d97706', 'at-risk': '#ea580c', churned: '#dc2626' };
const TIER_BG    = { healthy: '#dcfce7', watch: '#fef3c7', 'at-risk': '#ffedd5', churned: '#fee2e2' };
const TIER_LABEL = { healthy: 'Healthy', watch: 'Watch', 'at-risk': 'At risk', churned: 'Churned' };

const AMP_TABS = [
  { key: 'all',       label: 'Overall',     color: '#6366f1' },
  { key: 'chat',      label: 'Chat',        color: '#3b82f6' },
  { key: 'workflows', label: 'Workflows',   color: '#8b5cf6' },
  { key: 'live',      label: 'Live Events', color: '#f59e0b' },
  { key: 'protege',   label: 'Protege',     color: '#10b981' },
];

function monthShort(ym) {
  const [, m] = ym.split('-');
  return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m,10)-1];
}

function MetricSparkline({ values, months, color, onBarClick, activeBar }) {
  const H = 48, barW = 26, gap = 5;
  const W = months.length * (barW + gap) - gap;
  const maxVal = Math.max(...values, 1);
  const now = new Date();
  const currentM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return (
    <svg width={W} height={H + 16} style={{ display: 'block', overflow: 'visible' }}>
      {values.map((val, i) => {
        const x      = i * (barW + gap);
        const barH   = val > 0 ? Math.max(4, Math.round((val / maxVal) * H)) : 3;
        const y      = H - barH;
        const isCur  = months[i] === currentM;
        const isDrill = months[i] === activeBar;
        const isRisk = i > 0 && val > 0 && values[i-1] >= 3 && val < values[i-1] * 0.5;
        const fill   = isDrill ? color
          : val === 0 ? '#e2e8f0'
          : isRisk ? '#fca5a5'
          : isCur ? color
          : color + '99';
        return (
          <g key={months[i]}
             onClick={onBarClick ? () => onBarClick(months[i]) : undefined}
             style={{ cursor: onBarClick ? 'pointer' : 'default' }}>
            {/* transparent hit area so even zero-height bars are clickable */}
            {onBarClick && <rect x={x} y={0} width={barW} height={H} fill="transparent" />}
            <rect x={x} y={y} width={barW} height={barH} rx={3} fill={fill}
              style={{ opacity: isDrill ? 1 : undefined }} />
            {val > 0 && <text x={x + barW/2} y={y - 3} textAnchor="middle" fontSize={9} fill="#94a3b8">{val}</text>}
            <text x={x + barW/2} y={H + 13} textAnchor="middle" fontSize={9}
              fill={isDrill || isCur ? color : '#94a3b8'}
              fontWeight={isDrill || isCur ? 700 : 400}>
              {monthShort(months[i])}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function weekLabel(weekStart) {
  const d = new Date(String(weekStart) + 'T00:00:00');
  return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]} ${d.getDate()}`;
}

function WeeklySparkline({ weekRows, color, getVal, month }) {
  const H = 48, barW = 26, gap = 5;
  if (!weekRows || weekRows.length === 0) {
    return <div style={{ color: '#94a3b8', fontSize: 12, paddingTop: 8 }}>No weekly data for {monthShort(month)}</div>;
  }
  const values = weekRows.map(getVal);
  const maxVal = Math.max(...values, 1);
  const W = weekRows.length * (barW + gap) - gap;
  return (
    <svg width={W} height={H + 16} style={{ display: 'block', overflow: 'visible' }}>
      {weekRows.map((row, i) => {
        const val  = values[i];
        const x    = i * (barW + gap);
        const barH = val > 0 ? Math.max(4, Math.round((val / maxVal) * H)) : 3;
        const y    = H - barH;
        const fill = val === 0 ? '#e2e8f0' : color;
        return (
          <g key={row.week_start}>
            <rect x={x} y={y} width={barW} height={barH} rx={3} fill={fill} />
            {val > 0 && <text x={x + barW/2} y={y - 3} textAnchor="middle" fontSize={9} fill="#94a3b8">{val}</text>}
            <text x={x + barW/2} y={H + 13} textAnchor="middle" fontSize={9} fill="#94a3b8">
              {weekLabel(row.week_start)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function MetricPanel({ values, months, color, currentLabel, prevLabel, totalLabel, onBarClick, drillMonth, weeklyRows, getWeekVal }) {
  const n = values.length;
  const cur  = values[n - 1] ?? 0;
  const prev = values[n - 2] ?? 0;
  const total6m = values.reduce((a, b) => a + b, 0);
  const trend = cur > prev ? `↑ +${cur - prev} vs last mo` : cur < prev ? `↓ −${prev - cur} vs last mo` : '→ flat vs last mo';
  const trendColor = cur > prev ? '#16a34a' : cur < prev ? '#dc2626' : '#94a3b8';
  return (
    <div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{currentLabel}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: cur > 0 ? color : '#cbd5e1', lineHeight: 1 }}>{cur}</div>
          <div style={{ fontSize: 11, color: trendColor, marginTop: 3, fontWeight: 600 }}>{trend}</div>
        </div>
        <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: 20 }}>
          <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{prevLabel}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: prev > 0 ? '#94a3b8' : '#e2e8f0', lineHeight: 1 }}>{prev}</div>
        </div>
        <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: 20 }}>
          <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{totalLabel}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: total6m > 0 ? '#475569' : '#e2e8f0', lineHeight: 1 }}>{total6m}</div>
        </div>
      </div>
      {drillMonth && weeklyRows != null
        ? <WeeklySparkline weekRows={weeklyRows} color={color} getVal={getWeekVal} month={drillMonth} />
        : <MetricSparkline values={values} months={months} color={color} onBarClick={onBarClick} activeBar={drillMonth} />
      }
    </div>
  );
}

function getSalesAction(score, breakdown) {
  const { trendPts, monthsInSystem } = breakdown;
  if ((monthsInSystem ?? 6) <= 2 && score < 70) return { label: 'New user — share onboarding resources', color: '#6366f1', bg: '#ede9fe' };
  if (score >= 70 && trendPts >= 20)            return { label: 'Expansion opportunity', color: '#16a34a', bg: '#dcfce7' };
  if (score >= 70)                               return { label: 'Healthy — nurture relationship', color: '#0284c7', bg: '#e0f2fe' };
  if (score >= 40 && trendPts <= 10)            return { label: 'Usage declining — schedule call', color: '#d97706', bg: '#fef3c7' };
  if (score >= 40)                               return { label: 'Monitor — check in soon', color: '#d97706', bg: '#fef3c7' };
  if (score >= 15)                               return { label: 'At risk — reach out now', color: '#ea580c', bg: '#ffedd5' };
  return { label: 'Churn risk — call urgently', color: '#dc2626', bg: '#fee2e2' };
}

function AmplitudeUsageCard({ ampData, globalStats, contactRows, contactWeeklyRows }) {
  const [tab, setTab] = useState('all');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [drillMonth, setDrillMonth] = useState(null);
  const { score, tier, breakdown, lastActiveMonth, monthlyValues, months: scoreMonths, currentMonthMsgs, avgMsgsPerMonth } = ampData;

  const months = last6Months();
  const now = new Date();
  const currentM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // Build per-metric monthly arrays from raw rows
  const getField = (field) => months.map((m) => {
    const row = (contactRows ?? []).find((r) => r.year_month === m);
    return row ? (Number(row[field]) || 0) : 0;
  });
  const chatValues     = getField('chat_messages');
  const protegeValues  = getField('protege_messages');
  const liveValues     = getField('live_events');
  const presetValues   = getField('preset_workflows');
  const customValues   = getField('custom_workflows');
  const workflowValues = months.map((_, i) => presetValues[i] + customValues[i]);
  const allValues      = months.map((_, i) => chatValues[i] + protegeValues[i] + workflowValues[i] + liveValues[i]);

  // vs all-user median (overall)
  let vsMedianLabel = null, vsMedianColor = '#94a3b8';
  if (globalStats?.median > 0 && currentMonthMsgs > 0) {
    const ratio = currentMonthMsgs / globalStats.median;
    if (ratio >= 1.5)      { vsMedianLabel = `↑ ${ratio.toFixed(1)}× median`;            vsMedianColor = '#16a34a'; }
    else if (ratio >= 0.8) { vsMedianLabel = `≈ median (${globalStats.median}/mo)`;       vsMedianColor = '#64748b'; }
    else                   { vsMedianLabel = `↓ below median (${globalStats.median}/mo)`; vsMedianColor = '#dc2626'; }
  }

  // Feature breadth — used in last 3 months
  const featureBreadth = [
    { key: 'chat',      label: 'Chat',        used: chatValues.slice(-3).some((v) => v > 0) },
    { key: 'workflows', label: 'Workflows',   used: workflowValues.slice(-3).some((v) => v > 0) },
    { key: 'live',      label: 'Live Events', used: liveValues.slice(-3).some((v) => v > 0) },
    { key: 'protege',   label: 'Protege',     used: protegeValues.slice(-3).some((v) => v > 0) },
  ];
  const featuresUsedCount = featureBreadth.filter((f) => f.used).length;

  // Last active detail — what they did in last active month
  const lastActiveRow = lastActiveMonth
    ? (contactRows ?? []).find((r) => r.year_month === lastActiveMonth)
    : null;
  const lastActiveDetail = lastActiveRow ? [
    lastActiveRow.chat_messages > 0 && `Chat ${lastActiveRow.chat_messages}`,
    ((lastActiveRow.preset_workflows ?? 0) + (lastActiveRow.custom_workflows ?? 0)) > 0 &&
      `Workflows ${(lastActiveRow.preset_workflows ?? 0) + (lastActiveRow.custom_workflows ?? 0)}`,
    lastActiveRow.live_events > 0 && `Live Events ${lastActiveRow.live_events}`,
    lastActiveRow.protege_messages > 0 && `Protege ${lastActiveRow.protege_messages}`,
  ].filter(Boolean).join(', ') : null;

  // Sales action recommendation
  const action = getSalesAction(score, breakdown);

  // Weekly drill-down helpers
  function getWeeksForMonth(month) {
    return (contactWeeklyRows ?? [])
      .filter((r) => r.week_start && String(r.week_start).slice(0, 7) === month)
      .sort((a, b) => String(a.week_start).localeCompare(String(b.week_start)));
  }

  function getWeekVal(row) {
    if (tab === 'chat')      return Number(row.chat_messages ?? 0);
    if (tab === 'workflows') return Number(row.preset_workflows ?? 0) + Number(row.custom_workflows ?? 0);
    if (tab === 'live')      return Number(row.live_events ?? 0);
    if (tab === 'protege')   return Number(row.protege_messages ?? 0);
    return Number(row.chat_messages ?? 0) + Number(row.protege_messages ?? 0) +
      Number(row.live_events ?? 0) + Number(row.preset_workflows ?? 0) + Number(row.custom_workflows ?? 0);
  }

  const drillWeeks = drillMonth ? getWeeksForMonth(drillMonth) : null;

  function handleBarClick(month) {
    setDrillMonth((prev) => (prev === month ? null : month));
  }

  const activeTab = AMP_TABS.find((t) => t.key === tab) ?? AMP_TABS[0];

  return (
    <div style={{ background: '#f8fafc', borderRadius: 12, padding: '14px 16px', marginBottom: 20, border: '1px solid #e2e8f0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.6px' }}>
          Amplitude Usage
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {vsMedianLabel && (
            <span style={{ fontSize: 11, fontWeight: 600, color: vsMedianColor }}>{vsMedianLabel}</span>
          )}
          <span
            onClick={() => setShowBreakdown((v) => !v)}
            style={{
              fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 6,
              color: TIER_COLOR[tier], background: TIER_BG[tier],
              cursor: 'pointer', userSelect: 'none',
            }}
          >{score} — {TIER_LABEL[tier]} {showBreakdown ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Sales action recommendation */}
      <div style={{ marginBottom: 10 }}>
        <span style={{
          fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 6,
          color: action.color, background: action.bg, display: 'inline-block',
        }}>
          → {action.label}
        </span>
      </div>

      {/* Score breakdown */}
      {showBreakdown && breakdown && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          {[
            { label: 'Recency',     pts: breakdown.recencyPts,    max: 30, note: breakdown.recencyLabel },
            { label: 'Volume',      pts: breakdown.volumePts,      max: 25, note: `${breakdown.recentMsgs} msgs` },
            { label: 'Trend',       pts: breakdown.trendPts,       max: 25, note: breakdown.trendLabel },
            { label: 'Consistency', pts: breakdown.consistencyPts, max: 20, note: `${breakdown.activeCount}/${breakdown.monthsInSystem ?? 6} mo active` },
          ].map(({ label, pts, max, note }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ width: 80, fontSize: 11, color: '#64748b', flexShrink: 0 }}>{label}</span>
              <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 99, height: 6, overflow: 'hidden' }}>
                <div style={{ width: `${(pts / max) * 100}%`, height: '100%', borderRadius: 99,
                  background: pts/max >= 0.7 ? '#16a34a' : pts/max >= 0.4 ? '#d97706' : '#ef4444' }} />
              </div>
              <span style={{ width: 34, fontSize: 11, fontWeight: 700, textAlign: 'right', flexShrink: 0, color: TIER_COLOR[tier] }}>{pts}/{max}</span>
              <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0, minWidth: 90 }}>{note}</span>
            </div>
          ))}
          {/* Feature breadth */}
          <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 8 }}>
            <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
              Feature breadth — {featuresUsedCount}/4 used (last 3 mo)
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {featureBreadth.map(({ key, label, used }) => (
                <span key={key} style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                  background: used ? '#dcfce7' : '#f1f5f9',
                  color: used ? '#16a34a' : '#94a3b8',
                  border: `1px solid ${used ? 'rgba(34,197,94,.3)' : '#e2e8f0'}`,
                }}>
                  {used ? '✓' : '✗'} {label}
                </span>
              ))}
            </div>
            {featuresUsedCount < 4 && (
              <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 5 }}>
                Unused features = talking points
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {lastActiveMonth
                ? <>Last active: {monthShort(lastActiveMonth)}{lastActiveDetail ? ` — ${lastActiveDetail}` : ''}</>
                : 'No activity yet'}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: TIER_COLOR[tier] }}>Total: {score}/100</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
        {AMP_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setDrillMonth(null); }}
            style={{
              fontSize: 12, fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? t.color : '#94a3b8',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '4px 10px 8px', borderBottom: tab === t.key ? `2px solid ${t.color}` : '2px solid transparent',
              marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Drill-down breadcrumb */}
      {drillMonth && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <button
            onClick={() => setDrillMonth(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: activeTab.color, fontWeight: 600, fontSize: 12, padding: 0 }}
          >
            ← Monthly view
          </button>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>/ {monthShort(drillMonth)} (weekly)</span>
        </div>
      )}

      {/* Tab content */}
      {tab === 'all' && (
        <div>
          <MetricPanel
            values={allValues}
            months={months}
            color="#6366f1"
            currentLabel="This month (all)"
            prevLabel="Last month"
            totalLabel="6-month total"
            onBarClick={contactWeeklyRows ? handleBarClick : undefined}
            drillMonth={drillMonth}
            weeklyRows={drillWeeks}
            getWeekVal={getWeekVal}
          />
          {!drillMonth && avgMsgsPerMonth > 0 && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Avg {avgMsgsPerMonth}/mo (active months only)</div>
          )}
        </div>
      )}

      {tab === 'chat' && (
        <MetricPanel
          values={chatValues}
          months={months}
          color="#3b82f6"
          currentLabel="Chat msgs this mo"
          prevLabel="Last month"
          totalLabel="6-month total"
          onBarClick={contactWeeklyRows ? handleBarClick : undefined}
          drillMonth={drillMonth}
          weeklyRows={drillWeeks}
          getWeekVal={getWeekVal}
        />
      )}

      {tab === 'workflows' && (
        <div>
          <MetricPanel
            values={workflowValues}
            months={months}
            color="#8b5cf6"
            currentLabel="Workflows this mo"
            prevLabel="Last month"
            totalLabel="6-month total"
            onBarClick={contactWeeklyRows ? handleBarClick : undefined}
            drillMonth={drillMonth}
            weeklyRows={drillWeeks}
            getWeekVal={getWeekVal}
          />
          {!drillMonth && workflowValues[workflowValues.length - 1] > 0 && (
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: '#a78bfa', display: 'inline-block' }} />
                <span style={{ color: '#64748b' }}>Preset: <strong>{presetValues[presetValues.length - 1]}</strong></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: '#7c3aed', display: 'inline-block' }} />
                <span style={{ color: '#64748b' }}>Custom: <strong>{customValues[customValues.length - 1]}</strong></span>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'live' && (
        <MetricPanel
          values={liveValues}
          months={months}
          color="#f59e0b"
          currentLabel="Live events this mo"
          prevLabel="Last month"
          totalLabel="6-month total"
          onBarClick={contactWeeklyRows ? handleBarClick : undefined}
          drillMonth={drillMonth}
          weeklyRows={drillWeeks}
          getWeekVal={getWeekVal}
        />
      )}

      {tab === 'protege' && (
        <MetricPanel
          values={protegeValues}
          months={months}
          color="#10b981"
          currentLabel="Protege msgs this mo"
          prevLabel="Last month"
          totalLabel="6-month total"
          onBarClick={contactWeeklyRows ? handleBarClick : undefined}
          drillMonth={drillMonth}
          weeklyRows={drillWeeks}
          getWeekVal={getWeekVal}
        />
      )}
    </div>
  );
}

// ── ContactDetailsGrid ────────────────────────────────────────────────────────

function splitTags(val) {
  return String(val ?? '').split(',').map((s) => s.trim()).filter(Boolean);
}

function TagList({ value }) {
  const tags = splitTags(value);
  if (!tags.length) return <span style={{ color: 'var(--c-text-3)' }}>—</span>;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      {tags.map((t) => (
        <span key={t} style={{
          fontSize: 11, padding: '1px 6px', borderRadius: 4,
          background: 'var(--c-bg-2, #f1f5f9)', color: 'var(--c-text-2)',
          border: '1px solid var(--c-border)',
        }}>{t}</span>
      ))}
    </span>
  );
}

function DetailRow({ label, children }) {
  return (
    <div style={{ display: 'contents' }}>
      <div style={{ color: 'var(--c-text-3)', fontSize: 11.5, fontWeight: 600, paddingRight: 8, whiteSpace: 'nowrap', alignSelf: 'start', paddingTop: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: 'var(--c-text)', wordBreak: 'break-word' }}>
        {children}
      </div>
    </div>
  );
}

function ContactDetailsGrid({ contact }) {
  const location  = [contact.city, contact.state_province, contact.country_territory].filter(Boolean).join(', ');
  const addedDate = contact.added_date ? formatDate(contact.added_date) : '';
  const focusIndustry = [contact.focus_industry_macro, contact.focus_industry_mid, contact.focus_industry_micro].filter(Boolean).join(' › ');

  // Collect only rows that have at least one value
  // Product sector (IMP-1843): derived by the app server from the user's
  // default watchlist; BigDough's coverage tags stay as their own row.
  const productSector = contact._sector
    ? `${contact._sector}${contact._sector_pack ? ` (${contact._sector_pack})` : ''}`
    : '';
  const rows = [
    { label: 'Sector',        content: productSector,                plain: true },
    { label: 'Coverage',      content: contact.sector_coverage,      tags: true  },
    { label: 'Focus Industry',content: focusIndustry,                plain: true },
    { label: 'Job Function',  content: contact.job_function,         tags: true  },
  ].filter(({ content }) => {
    if (!content) return false;
    const s = String(content ?? '').trim();
    return s.length > 0;
  });

  if (!rows.length) return null;

  return (
    <div style={{
      marginTop: 10,
      display: 'grid',
      gridTemplateColumns: 'max-content 1fr',
      gap: '5px 0',
      rowGap: 5,
      padding: '10px 12px',
      background: 'var(--c-bg-2, #f8fafc)',
      borderRadius: 8,
      border: '1px solid var(--c-border)',
    }}>
      {rows.map(({ label, content, tags }) => (
        <DetailRow key={label} label={label}>
          {tags ? <TagList value={content} /> : <span style={{ color: content ? 'var(--c-text)' : 'var(--c-text-3)' }}>{content || '—'}</span>}
        </DetailRow>
      ))}
    </div>
  );
}

// ── Tag colors by semantic prefix ────────────────────────────────────────────
const TAG_COLOR_MAP = [
  [/^(budget|cost|price|expensive|cheap)/i,  { bg: '#fef9c3', text: '#854d0e', border: '#fde047' }],
  [/^(q[1-4]|timeline|deadline|date|by-|until)/i, { bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd' }],
  [/^(interested|interest|fan|loves|bullish|excited)/i, { bg: '#dcfce7', text: '#15803d', border: '#86efac' }],
  [/^(cold|warm|hot|stage)/i,                { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' }],
  [/^(cautious|slow|risk|concern|worry|objection|hesit)/i, { bg: '#fff7ed', text: '#c2410c', border: '#fdba74' }],
  [/^(founder|ceo|cto|cfo|vp|head|director|pm)/i, { bg: '#faf5ff', text: '#7e22ce', border: '#d8b4fe' }],
  [/^(macro|micro|quant|fundamental|value|growth|momentum)/i, { bg: '#f0f9ff', text: '#0369a1', border: '#7dd3fc' }],
];

function tagStyle(tag) {
  for (const [re, style] of TAG_COLOR_MAP) {
    if (re.test(tag)) return style;
  }
  return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
}

function AiTagChips({ tagsStr }) {
  if (!tagsStr) return null;
  const tags = tagsStr.split(',').map((t) => t.trim()).filter(Boolean);
  if (!tags.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {tags.map((tag) => {
        const s = tagStyle(tag);
        return (
          <span key={tag} style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
            background: s.bg, color: s.text, border: `1px solid ${s.border}`,
          }}>
            {tag}
          </span>
        );
      })}
    </div>
  );
}

function NotesCard({ contact, getToken, onContactUpdate }) {
  // raw notes live in the existing `notes` column
  const [draft, setDraft]         = useState(contact.notes ?? '');
  const [saving, setSaving]       = useState(false);
  const [saveOk, setSaveOk]       = useState(false);
  const [error, setError]         = useState('');
  const [aiTags, setAiTags]       = useState(contact.ai_tags ?? '');
  const [aiSummary, setAiSummary] = useState(contact.ai_summary ?? '');
  const [aiError, setAiError]     = useState('');
  const [expanded, setExpanded]   = useState(!!(contact.notes || contact.ai_tags));

  useEffect(() => {
    setDraft(contact.notes ?? '');
    setAiTags(contact.ai_tags ?? '');
    setAiSummary(contact.ai_summary ?? '');
    setExpanded(!!(contact.notes || contact.ai_tags));
    setError(''); setAiError(''); setSaveOk(false);
  }, [contact.contact_id]);

  const isDirty = draft !== (contact.notes ?? '');

  async function handleSave() {
    if (!draft.trim()) return;
    setSaving(true); setError(''); setAiError(''); setSaveOk(false);
    try {
      const res = await saveContactNotes(getToken, contact.contact_id, draft);
      // Notes always saved; AI is best-effort
      setAiTags(res.ai_tags ?? aiTags);
      setAiSummary(res.ai_summary ?? aiSummary);
      if (res.ai_error) setAiError('Tags couldn\'t be extracted this time.');
      setSaveOk(true);
      onContactUpdate?.({ ...contact, notes: draft, ai_tags: res.ai_tags ?? contact.ai_tags, ai_summary: res.ai_summary ?? contact.ai_summary });
    } catch (e) {
      setError(e.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const hasAi = aiTags || aiSummary;
  const noteStats = [
    aiSummary ? 'Summary present' : 'No summary',
    aiTags ? `${aiTags.split(',').filter(Boolean).length} tags` : 'No tags',
  ];

  return (
    <div style={{ border: '1px solid var(--c-border)', borderRadius: 10, marginBottom: 20, overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: 'var(--c-bg-2, #f8fafc)',
          border: 'none', cursor: 'pointer', textAlign: 'left',
          borderBottom: expanded ? '1px solid var(--c-border)' : 'none',
        }}
      >
        <span style={{ fontSize: 13 }}>📝</span>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--c-text-2)', flex: 1 }}>Notes</span>
        {hasAi && !expanded && aiSummary && (
          <span style={{ fontSize: 11.5, color: 'var(--c-text-3)', fontStyle: 'italic', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {aiSummary}
          </span>
        )}
        {hasAi && !expanded && <AiTagChips tagsStr={aiTags} />}
        <span style={{ fontSize: 10, color: 'var(--c-text-3)', flexShrink: 0 }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div style={{ padding: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {noteStats.map((item) => (
              <span key={item} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--c-bg-2, #f8fafc)', color: 'var(--c-text-3)', border: '1px solid var(--c-border)' }}>
                {item}
              </span>
            ))}
          </div>
          {aiSummary && (
            <div style={{
              fontSize: 12.5, fontStyle: 'italic', color: 'var(--c-text-2)',
              padding: '8px 12px', background: 'var(--c-bg-2,#f8fafc)',
              borderRadius: 7, marginBottom: 10,
              borderLeft: '3px solid var(--c-primary, #6366f1)',
            }}>
              {aiSummary}
            </div>
          )}
          {aiTags && <div style={{ marginBottom: 10 }}><AiTagChips tagsStr={aiTags} /></div>}

          <textarea
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setSaveOk(false); }}
            placeholder="Write anything — budget constraints, personality, timing, follow-up hooks… Claude will extract tags and a summary."
            rows={4}
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 13, lineHeight: 1.5,
              padding: '8px 10px', borderRadius: 7, border: '1px solid var(--c-border)',
              background: 'var(--c-bg, #fff)', color: 'var(--c-text)',
              resize: 'vertical', fontFamily: 'inherit', outline: 'none',
            }}
          />

          {error   && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{error}</div>}
          {aiError && <div style={{ color: '#d97706', fontSize: 11.5, marginTop: 4 }}>⚠ {aiError}</div>}
          {saveOk && !error && <div style={{ color: '#16a34a', fontSize: 12, marginTop: 4 }}>✓ Saved</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, gap: 8 }}>
            {isDirty && (
              <button className="btn btn--ghost btn--sm" onClick={() => { setDraft(contact.notes ?? ''); setSaveOk(false); }}>
                Discard
              </button>
            )}
            <button
              className="btn btn--primary btn--sm"
              onClick={handleSave}
              disabled={saving || !draft.trim() || !isDirty}
            >
              {saving ? 'Saving…' : hasAi ? 'Save & re-process' : 'Save & extract tags'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parent-fund tag next to the firm name (pod shops, e.g. Santa Rosa →
 * Millennium). Shows "(Millennium parent)" when linked; click it — or the
 * "+ parent" affordance when unlinked — to pick/change/remove the parent
 * inline. Saves via PATCH /api/firms/:id { parent_firm_id }.
 */
function ParentFirmTag({ contact, firms, getToken, onContactUpdate }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);

  const firmId = contact.firm_id;
  const parentName = contact._parent_firm_name || '';
  if (!firmId || !getToken) return null;

  async function save(parentFirmId, parentFirmName) {
    setBusy(true); setError(null);
    try {
      await updateFirm(getToken, firmId, { parent_firm_id: parentFirmId });
      setEditing(false);
      onContactUpdate?.({ ...contact, _parent_firm_id: parentFirmId, _parent_firm_name: parentFirmName });
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  if (editing) {
    return (
      <span
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
      >
        <span style={{ width: 220, fontWeight: 400 }}>
          <FirmCombobox
            firms={(firms ?? []).filter((f) => f.firm_id !== firmId)}
            value=""
            onChange={(id, f) => { if (id) save(id, f?.firm_name ?? ''); }}
            placeholder={parentName ? `Parent: ${parentName}` : 'Search parent fund…'}
          />
        </span>
        {parentName && (
          <button
            onClick={() => save('', '')}
            disabled={busy}
            title="Unlink from parent fund"
            style={{ background: 'transparent', border: '1px solid var(--c-border)', borderRadius: 5, padding: '2px 7px', cursor: busy ? 'default' : 'pointer', fontSize: 11, color: 'var(--c-red, #dc2626)' }}
          >
            Remove
          </button>
        )}
        <button
          onClick={() => setEditing(false)}
          disabled={busy}
          style={{ background: 'transparent', border: 'none', cursor: busy ? 'default' : 'pointer', fontSize: 11, color: 'var(--c-text-3)' }}
        >
          Cancel
        </button>
        {busy && <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Saving…</span>}
        {error && <span style={{ fontSize: 11, color: 'var(--c-red, #dc2626)' }}>{error}</span>}
      </span>
    );
  }

  return parentName ? (
    <button
      onClick={() => setEditing(true)}
      title="Change or remove the parent fund"
      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: 'var(--c-text-3)' }}
    >
      ({parentName} parent)
    </button>
  ) : (
    <button
      onClick={() => setEditing(true)}
      title="Link this firm to a parent fund (pod shops, e.g. Millennium)"
      style={{ background: 'transparent', border: '1px dashed var(--c-border)', borderRadius: 4, padding: '0px 6px', cursor: 'pointer', fontSize: 11, fontWeight: 400, color: 'var(--c-text-3)' }}
    >
      + parent
    </button>
  );
}

export default function ContactTimeline({ contact, data, getToken, onViewInContacts, amplitudeScores, amplitudeGlobalStats, amplitudeUserMonthly, amplitudeUserWeekly, onContactUpdate }) {
  if (!contact) return <div className="empty-state"><div className="empty-state__sub">No contact selected.</div></div>;

  const { demos = [], trials = [] } = data ?? {};
  const contactId = contact.contact_id ?? '';
  const name = contact.name ?? contact.email ?? '—';

  // ── Fetch this contact's outreach log directly from the API ───────────────
  const [contactOutreach, setContactOutreach] = useState([]);
  const [outreachLoading, setOutreachLoading] = useState(true);
  const [zoomNotes, setZoomNotes] = useState([]);

  // ── Add-to-outreach (pin) ─────────────────────────────────────────────────
  const [pinned, setPinned] = useState(!!contact.gtm_pinned);
  const [pinning, setPinning] = useState(false);
  useEffect(() => { setPinned(!!contact.gtm_pinned); }, [contact.contact_id, contact.gtm_pinned]);
  async function handleTogglePin() {
    if (!getToken || !contactId || pinning) return;
    const next = !pinned;
    setPinning(true);
    setPinned(next); // optimistic
    try {
      await setContactPinned(getToken, contactId, next);
      onContactUpdate?.({ ...contact, gtm_pinned: next });
      broadcastOutreachRefresh();
    } catch (e) {
      setPinned(!next); // revert on failure
      console.error('[ContactTimeline] pin failed', e);
    } finally {
      setPinning(false);
    }
  }

  useEffect(() => {
    if (!contactId || !getToken) return;
    let alive = true;
    setOutreachLoading(true);
    setContactOutreach([]);
    Promise.all([
      fetchOutreachLog(getToken, { contactId, limit: 0 }),
      fetchZoomNotesByContact(getToken, contactId).catch(() => ({ notes: [] })),
    ]).then(([outreach, zoom]) => {
      if (!alive) return;
      setContactOutreach(outreach.rows ?? []);
      setZoomNotes(zoom.notes ?? []);
    }).catch(() => {}).finally(() => { if (alive) setOutreachLoading(false); });
    return () => { alive = false; };
  }, [contactId, getToken]);

  const stage = computeStage(contact, demos, trials, contactOutreach);

  // ── Build unified event list ──────────────────────────────────────────────
  const events = useMemo(() => {
    const list = [];

    // Outreach log events fetched directly for this contact
    for (const row of contactOutreach) {
      const dir = (row.direction ?? '').toLowerCase();
      const ch  = (row.channel ?? '').toLowerCase();
      const type = dir === 'inbound' ? 'email_inbound'
        : ch === 'bigdough' ? 'bigdough'
        : 'email_outbound';

      list.push({
        type,
        direction: dir,
        date: row.date ?? '',
        title: row.subject || '(no subject)',
        meta: [
          row.opened  === 'True' && '✓ Opened',
          row.clicked === 'True' && '✓ Clicked',
          row.replied === 'True' && '✓ Replied',
        ].filter(Boolean).join('  ·  '),
        body:           row.body ?? '',
        body_html:      row.body_html ?? '',
        body_format:    row.body_format ?? '',
        body_truncated: !!row.body_truncated,
        gmail_thread_id: row.gmail_thread_id ?? '',
        bd_send_id:     row.bd_send_id ?? '',
        log_id:         row.log_id ?? '',
      });
    }

    // Demos — match by contact_id OR by attendee_emails containing this contact's email
    const contactEmail = String(contact.email ?? '').toLowerCase().trim();
    const seenDemoIds = new Set();
    for (const row of demos) {
      const matchesById = row.contact_id === contactId;
      const attendeeEmails = String(row.attendee_emails ?? '')
        .split(',').map((e) => e.trim().toLowerCase()).filter(Boolean);
      const matchesByEmail = contactEmail && attendeeEmails.includes(contactEmail);
      if (!matchesById && !matchesByEmail) continue;
      if (seenDemoIds.has(row.demo_id)) continue;
      seenDemoIds.add(row.demo_id);

      const dStatus = (row.status ?? '').toLowerCase();
      const type = dStatus === 'no-show' ? 'demo_noshow'
        : dStatus === 'completed' ? 'demo_completed'
        : 'demo_scheduled';

      const demoLabel = row.event_title ? `Demo: ${row.event_title}` : `Demo — ${row.status ?? 'Scheduled'}`;
      list.push({
        type,
        date: row.scheduled_date ?? row.date ?? '',
        title: demoLabel,
        meta: [
          row.outcome   && `Outcome: ${row.outcome}`,
          row.next_step && `Next: ${row.next_step}`,
          row.notes,
        ].filter(Boolean).join('  ·  '),
      });
    }

    // Trials — match by contact_id; use end_date for renewal display
    for (const row of trials) {
      if (row.contact_id !== contactId) continue;
      const tStatus = (row.status ?? '').toLowerCase();
      const type = tStatus === 'active' ? 'trial_active'
        : tStatus === 'converted' ? 'trial_ended'
        : tStatus === 'churned'   ? 'trial_ended'
        : 'trial_started';

      const health = computeHealth(row);
      list.push({
        type,
        date: row.start_date ?? '',
        title: `Trial — ${row.status ?? 'Active'}`,
        meta: [
          row.end_date && `Ends ${formatDate(row.end_date)}`,
          `Health: ${health.label}`,
          health.loginDays !== null && `Last login ${health.loginDays}d ago`,
        ].filter(Boolean).join('  ·  '),
      });
    }

    // Zoom AI meeting notes
    for (const n of zoomNotes) {
      const ts = n.received_at?.value ?? n.received_at ?? '';
      let extracted = { items: [], attendees: [] };
      try {
        const p = JSON.parse(n.action_items ?? '{}');
        extracted = Array.isArray(p)
          ? { items: p.map(s => ({ assignee: '', task: s })), attendees: [] }
          : { items: p.items ?? [], attendees: p.attendees ?? [] };
      } catch {}
      list.push({
        type: 'zoom_meeting',
        date: ts,
        title: n.meeting_name || n.subject || 'Zoom meeting',
        meta: extracted.attendees.join(', '),
        body: n.summary || '',
        zoomItems: extracted.items,
      });
    }

    // Sort newest first
    list.sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      return db - da;
    });

    return list;
  }, [contactOutreach, demos, trials, contactId]);

  // ── Group Gmail threads ───────────────────────────────────────────────────
  // Items with the same gmail_thread_id are collapsed into one thread group.
  // Non-email events and BigDough emails stay as individual items.
  const timelineItems = useMemo(() => {
    const items = [];
    const threadMap = new Map(); // threadId → index in items

    for (const ev of events) {
      const tid = ev.gmail_thread_id;
      if (tid && (ev.type === 'email_outbound' || ev.type === 'email_inbound')) {
        if (threadMap.has(tid)) {
          // Add to existing thread group
          items[threadMap.get(tid)].threadMessages.push(ev);
        } else {
          // Create new thread group (first = newest message in thread)
          const idx = items.length;
          threadMap.set(tid, idx);
          items.push({ ...ev, isThread: true, threadMessages: [ev] });
        }
      } else {
        items.push({ ...ev, isThread: false, threadMessages: [] });
      }
    }

    return items;
  }, [events]);

  // ── Active trial for health card ─────────────────────────────────────────
  const activeTrial = trials.find(
    (t) => t.contact_id === contactId && (t.status ?? '').toLowerCase() === 'active'
  );
  const health = activeTrial ? computeHealth(activeTrial) : null;

  // ── Trial backing the usage heatmap/journal section (IMP-1847) ───────────
  // Any linked trial works (the journal is keyed by trial_id): prefer Active,
  // then Converted (customers), newest first within a status.
  const usageTrial = useMemo(() => {
    const mine = trials.filter((t) =>
      t.contact_id === contactId ||
      String(t.contact_ids ?? '').split(',').map((s) => s.trim()).includes(contactId)
    );
    const rank = { active: 0, converted: 1 };
    return [...mine].sort((a, b) => {
      const ra = rank[(a.status ?? '').toLowerCase()] ?? 2;
      const rb = rank[(b.status ?? '').toLowerCase()] ?? 2;
      if (ra !== rb) return ra - rb;
      return String(b.start_date ?? '').localeCompare(String(a.start_date ?? ''));
    })[0] ?? null;
  }, [trials, contactId]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Contact header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: 'var(--c-primary)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 700, flexShrink: 0,
          }}>
            {name.trim().split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{name}</div>
              {onViewInContacts && (
                <button
                  onClick={() => onViewInContacts(contact)}
                  title="View in Contacts table"
                  style={{ background: 'transparent', border: '1px solid var(--c-border)', borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontSize: 13, color: 'var(--c-text-2)', lineHeight: 1.4 }}
                >
                  ↗ Contacts
                </button>
              )}
              <button
                onClick={handleTogglePin}
                disabled={pinning}
                title={pinned ? 'Remove from outreach list' : 'Add to outreach list'}
                style={{
                  background: pinned ? 'var(--c-primary)' : 'transparent',
                  border: `1px solid ${pinned ? 'var(--c-primary)' : 'var(--c-border)'}`,
                  borderRadius: 6, padding: '2px 8px', cursor: pinning ? 'default' : 'pointer',
                  fontSize: 13, fontWeight: 600, lineHeight: 1.4,
                  color: pinned ? '#fff' : 'var(--c-text-2)',
                }}
              >
                {pinned ? '✓ In outreach' : '+ Outreach'}
              </button>
            </div>
            {(contact.role ?? contact.title) && (
              <div style={{ color: 'var(--c-text-2)', fontSize: 13 }}>
                {contact.role ?? contact.title}
              </div>
            )}
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--c-text-2)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {contact._firm_name ?? contact.firm_name ?? <span style={{ color: 'var(--c-text-3)', fontWeight: 400 }}>No firm</span>}
              <ParentFirmTag contact={contact} firms={data?.firms} getToken={getToken} onContactUpdate={onContactUpdate} />
              {(contact._fund_type ?? contact.fund_type) && (
                <span style={{ fontSize: 11, fontWeight: 500, padding: '1px 6px', borderRadius: 4, background: 'var(--c-bg-2, #f1f5f9)', color: 'var(--c-text-3)', border: '1px solid var(--c-border)' }}>
                  {contact._fund_type ?? contact.fund_type}
                </span>
              )}
            </div>
            <div style={{ color: 'var(--c-text-3)', fontSize: 12, marginTop: 1 }}>{contact.email}</div>
          </div>
        </div>

        {/* Stage + status chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className={`badge ${STAGE_BADGE_CLASS[stage] ?? 'badge-cold'}`}>
            {STAGE_LABELS[stage] ?? stage}
          </span>
          {health && (
            <span className={`badge badge-health-${health.score}`}>
              {health.label}
            </span>
          )}
          {amplitudeScores && contact.email && amplitudeScores[contact.email.toLowerCase().trim()] && (
            <AmplitudeScoreChip
              score={amplitudeScores[contact.email.toLowerCase().trim()].score}
              tier={amplitudeScores[contact.email.toLowerCase().trim()].tier}
              breakdown={amplitudeScores[contact.email.toLowerCase().trim()].breakdown}
              showLabel
            />
          )}
          {contact.bd_status && (
            <span style={{ fontSize: 12, background: 'var(--c-surface-2, #f1f5f9)', border: '1px solid var(--c-border)', borderRadius: 5, padding: '2px 7px', color: 'var(--c-text-2)', fontWeight: 500 }}>
              {contact.bd_status}
            </span>
          )}
        </div>
        {/* AI deal score — single source of truth for tier / next step / why /
            notes / dimensions (the old duplicate "Decision card" was removed). */}
        <DealScoreCard contact={contact} activeTrial={activeTrial} getToken={getToken} onContactUpdate={onContactUpdate} />
        {/* Contact details grid */}
        <ContactDetailsGrid contact={contact} />
      </div>

      {/* Notes + AI tags */}
      <NotesCard contact={contact} getToken={getToken} onContactUpdate={onContactUpdate} />

      {/* Quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 24 }}>
        {[
          { label: 'Events', value: outreachLoading ? '…' : events.length },
          { label: 'Last Touch', value: (() => {
            const ts = events.map(e => new Date(e.date || 0).getTime()).filter(t => !isNaN(t) && t > 0);
            const max = ts.length ? Math.max(...ts) : 0;
            return max > 0 ? daysAgo(new Date(max).toISOString()) : (daysAgo(contact._lastTouchDate) ?? '—');
          })() },
          { label: 'Firm ARR', value: contact._arr > 0 ? `$${(contact._arr / 1000).toFixed(0)}K` : '—' },
          { label: 'Firm AUM', value: (() => {
            const aum = contact._firm_aum;
            if (!aum) return '—';
            const n = Number(aum);
            if (isNaN(n)) return String(aum);
            if (n >= 1000) return `$${(n / 1000).toFixed(1)}B`;
            return `$${n.toFixed(0)}M`;
          })() },
        ].map((s) => (
          <div key={s.label} style={{
            background: '#f8fafc', borderRadius: 8, padding: '10px 12px', textAlign: 'center'
          }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{s.value}</div>
            <div style={{ color: 'var(--c-text-2)', fontSize: 11, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Usage heatmap + journal (IMP-1847) — any contact with a linked trial,
          plus customers without one (journal keyed 'contact:<id>' — Clerk-
          merged customers have no trial row). Sits above the Amplitude usage
          card (which may be removed later). */}
      {(usageTrial || (stage === 'customer' && contact.email)) && (
        <div style={{ marginBottom: 24 }}>
          <UsageJournalPanel
            trial={usageTrial ?? { trial_id: `contact:${contactId}` }}
            getToken={getToken}
          />
        </div>
      )}

      {/* Amplitude usage card — shown for any contact with amplitude data */}
      {amplitudeScores && contact.email && amplitudeScores[contact.email.toLowerCase().trim()] && (
        <AmplitudeUsageCard
          ampData={amplitudeScores[contact.email.toLowerCase().trim()]}
          globalStats={amplitudeGlobalStats}
          contactRows={(amplitudeUserMonthly ?? []).filter(
            (r) => String(r.user_id ?? '').toLowerCase().trim() === contact.email.toLowerCase().trim()
          )}
          contactWeeklyRows={(amplitudeUserWeekly ?? []).length > 0
            ? (amplitudeUserWeekly ?? []).filter(
                (r) => String(r.user_id ?? '').toLowerCase().trim() === contact.email.toLowerCase().trim()
              )
            : null}
        />
      )}

      {/* Live Lumosity watchlist — only when Clerk + lumosity_infra are configured */}
      {LUMOSITY_ENABLED && contact.email && (
        <LumosityWatchlistCard email={contact.email} />
      )}

      {/* Timeline */}
      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--c-text-2)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.4px' }}>
        Activity {outreachLoading ? '(…)' : `(${events.length})`}
      </div>

      {outreachLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '24px 0', color: 'var(--c-text-3)', fontSize: 13 }}>
          <div className="spinner" style={{ width: 16, height: 16 }} />
          Loading activity…
        </div>
      ) : events.length === 0 ? (
        <div className="empty-state" style={{ padding: '32px 0' }}>
          <div className="empty-state__icon">📭</div>
          <div className="empty-state__sub">No activity recorded yet</div>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Vertical line */}
          <div style={{
            position: 'absolute', left: 11, top: 6, bottom: 6,
            width: 2, background: 'var(--c-border)',
          }} />

          {timelineItems.map((ev, i) => (
            <TimelineItem key={ev.gmail_thread_id || ev.bd_send_id || i} ev={ev} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── DealScoreCard ────────────────────────────────────────────────────────────
const DEAL_TIER_COLORS = {
  Hot:     { bg: '#fee2e2', fg: '#b91c1c', bar: '#ef4444' },
  Warm:    { bg: '#ffedd5', fg: '#c2410c', bar: '#f97316' },
  Nurture: { bg: '#fef9c3', fg: '#a16207', bar: '#eab308' },
  Cold:    { bg: '#e0f2fe', fg: '#0369a1', bar: '#38bdf8' },
  Dead:    { bg: '#f1f5f9', fg: '#64748b', bar: '#94a3b8' },
};
const TREND_ARROW = { up: '↑', down: '↓', flat: '→' };
const DIM_LABELS = { fit: 'Fit', need: 'Need', intent: 'Intent', authority: 'Authority', momentum: 'Momentum', risk: 'Risk' };

function scrollToLog(logId) {
  const el = document.getElementById(`log-${logId}`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.style.transition = 'background 0.3s';
    el.style.background = 'var(--c-surface-2, #f1f5f9)';
    setTimeout(() => { el.style.background = ''; }, 1200);
  }
}

function DealScoreCard({ contact, activeTrial, getToken, onContactUpdate }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [local, setLocal] = useState(null); // freshly-refreshed result overrides contact.*
  const [savingAction, setSavingAction] = useState(false);
  const [nextAction, setNextAction] = useState(contact.next_action ?? '');
  const [nextActionDue, setNextActionDue] = useState(contact.next_action_due ?? '');

  const tier   = local?.deal_tier ?? contact.deal_tier ?? null;
  const score  = local?.deal_likelihood ?? contact.deal_likelihood ?? null;
  const trend  = local?.trend ?? contact.deal_trend ?? 'flat';
  const conf   = local?.confidence ?? contact.deal_confidence ?? 'low';
  const nextStep = local?.next_step ?? contact.deal_next_step ?? '';
  const why      = local?.why ?? contact.deal_why ?? '';
  const notesSummary = contact.ai_summary ?? '';
  const notesTags = contact.ai_tags ?? '';

  let dims = local?.dimensions ?? null;
  if (!dims && contact.deal_dimensions) { try { dims = JSON.parse(contact.deal_dimensions); } catch { dims = null; } }
  let basedOn = local?.based_on ?? null;
  if (!basedOn && contact.deal_based_on) { try { basedOn = JSON.parse(contact.deal_based_on); } catch { basedOn = null; } }

  useEffect(() => {
    setNextAction(contact.next_action ?? '');
    setNextActionDue(contact.next_action_due ?? '');
  }, [contact.contact_id, contact.next_action, contact.next_action_due]);

  async function handleRefresh() {
    if (!getToken || !contact.contact_id) return;
    setBusy(true); setErr('');
    try {
      const res = await refreshDealScore(getToken, contact.contact_id);
      setLocal(res);
      onContactUpdate?.({ ...contact,
        deal_tier: res.deal_tier, deal_likelihood: res.deal_likelihood,
        deal_trend: res.trend, deal_confidence: res.confidence,
        deal_dimensions: JSON.stringify(res.dimensions ?? {}),
        deal_next_step: res.next_step, deal_why: res.why,
        deal_based_on: JSON.stringify(res.based_on ?? []),
        ...(res.bd_status ? { bd_status: res.bd_status } : {}),
      });
    } catch (e) { setErr(e.message || 'Failed'); }
    finally { setBusy(false); }
  }

  async function handleSaveAction() {
    if (!contact.contact_id) return;
    setSavingAction(true);
    setErr('');
    try {
      await saveNextAction(getToken, contact.contact_id, nextAction.trim(), nextActionDue || '');
      onContactUpdate?.({
        ...contact,
        next_action: nextAction.trim(),
        next_action_due: nextActionDue || '',
      });
    } catch (e) {
      setErr(e.message || 'Failed to save next action');
    } finally {
      setSavingAction(false);
    }
  }

  const c = DEAL_TIER_COLORS[tier] ?? DEAL_TIER_COLORS.Cold;
  const muted = conf === 'low';

  return (
    <div style={{
      marginTop: 12, padding: '12px 14px', borderRadius: 8,
      border: '1px solid var(--c-border)', background: 'var(--c-surface, #fff)',
      opacity: muted ? 0.85 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--c-text-3)' }}>
          Deal likelihood
        </span>
        {tier ? (
          <>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 6, background: c.bg, color: c.fg }}>
              {tier} {TREND_ARROW[trend] ?? ''}
            </span>
            {score != null && (
              <span style={{ fontSize: 12, color: 'var(--c-text-2)', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(score)}/100
              </span>
            )}
            <span style={{ fontSize: 11, color: 'var(--c-text-3)' }} title="Confidence gates emphasis">
              {conf} confidence
            </span>
          </>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>Not scored yet</span>
        )}
        <button
          onClick={handleRefresh}
          disabled={busy}
          style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid var(--c-border)', borderRadius: 6, padding: '2px 9px', cursor: busy ? 'default' : 'pointer', fontSize: 12, color: 'var(--c-text-2)' }}
        >
          {busy ? 'Scoring…' : '↻ Refresh score'}
        </button>
      </div>

      {err && <div style={{ color: 'var(--c-danger, #dc2626)', fontSize: 12, marginTop: 6 }}>{err}</div>}

      {nextStep && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>Next step: </span>{nextStep}
        </div>
      )}
      {why && (
        <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--c-text-2)' }}>{why}</div>
      )}

      <div style={{
        marginTop: 10, padding: '10px 12px', borderRadius: 8,
        background: 'var(--c-bg-2, #f8fafc)', border: '1px solid var(--c-border)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--c-text-3)', marginBottom: 6 }}>
          Log next action
        </div>
        {notesSummary && (
          <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginBottom: 4 }}>
            {notesSummary}
          </div>
        )}
        {notesTags && (
          <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {String(notesTags).split(',').map((tag) => tag.trim()).filter(Boolean).slice(0, 5).map((tag) => (
              <span key={tag} style={{ fontSize: 10.5, padding: '1px 6px', borderRadius: 99, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>
                {tag}
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
          <input
            value={nextAction}
            onChange={(e) => setNextAction(e.target.value)}
            placeholder="Next action"
            style={{
              flex: '1 1 220px', minWidth: 220,
              padding: '6px 10px', borderRadius: 6, border: '1px solid var(--c-border)',
              background: 'var(--c-surface, #fff)', fontSize: 12,
            }}
          />
          <input
            type="date"
            value={nextActionDue}
            onChange={(e) => setNextActionDue(e.target.value)}
            style={{
              padding: '6px 10px', borderRadius: 6, border: '1px solid var(--c-border)',
              background: 'var(--c-surface, #fff)', fontSize: 12,
            }}
          />
          <button onClick={handleSaveAction} disabled={savingAction} className="btn btn--ghost btn--sm">
            {savingAction ? 'Saving…' : 'Save action'}
          </button>
        </div>
        {activeTrial && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--c-text-2)' }}>
            Trial: {activeTrial.status || 'active'}{activeTrial.end_date ? ` · ends ${formatDate(activeTrial.end_date)}` : ''}
          </div>
        )}
      </div>

      {dims && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
          {Object.entries(DIM_LABELS).map(([key, label]) => {
            const d = dims[key];
            if (!d) return null;
            const pct = Math.max(0, Math.min(5, Number(d.score) || 0)) / 5 * 100;
            return (
              <div key={key} title={d.note ?? ''} style={{ fontSize: 11 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--c-text-2)' }}>
                  <span>{label}{key === 'risk' ? ' (risk)' : ''}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{d.score}/5</span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: 'var(--c-border)', marginTop: 2 }}>
                  <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2, background: key === 'risk' ? '#ef4444' : c.bar }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {basedOn && basedOn.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Based on:</span>
          {basedOn.map((id, i) => (
            <button
              key={id}
              onClick={() => scrollToLog(id)}
              style={{ fontSize: 11, background: 'var(--c-surface-2, #f1f5f9)', border: '1px solid var(--c-border)', borderRadius: 5, padding: '1px 7px', cursor: 'pointer', color: 'var(--c-text-2)' }}
            >
              email {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TimelineItem ─────────────────────────────────────────────────────────────
function TimelineItem({ ev }) {
  const [expanded, setExpanded] = useState(false);
  const [threadOpen, setThreadOpen] = useState(false);
  const colors = eventColor(ev.type);
  const hasBody = ev.body || ev.body_html;
  const isZoom = ev.type === 'zoom_meeting';
  const isEmailType = ev.type === 'email_outbound' || ev.type === 'email_inbound' || ev.type === 'bigdough';
  const isThread = ev.isThread && ev.threadMessages?.length > 1;

  return (
    <div id={ev.log_id ? `log-${ev.log_id}` : undefined} style={{ display: 'flex', gap: 14, marginBottom: 18, position: 'relative' }}>
      {/* Colored dot */}
      <div style={{
        width: 24, height: 24, borderRadius: '50%',
        background: colors.dot,
        border: `2px solid ${colors.dot}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, flexShrink: 0, zIndex: 1,
      }}>
        {eventIcon(ev.type)}
      </div>

      <div style={{ flex: 1, paddingTop: 2, minWidth: 0 }}>
        {/* Title row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {/* Source badge */}
            {colors.label && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                padding: '1px 6px', borderRadius: 4,
                background: colors.badge, color: colors.badgeText,
                flexShrink: 0,
              }}>
                {colors.label}
              </span>
            )}
            {/* Thread count badge */}
            {isThread && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                background: '#f1f5f9', color: '#64748b', flexShrink: 0,
              }}>
                {ev.threadMessages.length} messages
              </span>
            )}
            <div
              style={{ fontWeight: 500, fontSize: 13.5, cursor: (hasBody || isThread || isZoom) ? 'pointer' : 'default', wordBreak: 'break-word' }}
              onClick={() => { if (hasBody || isZoom) setExpanded((v) => !v); else if (isThread) setThreadOpen((v) => !v); }}
            >
              {ev.title}
              {(hasBody || isThread || isZoom) && (
                <span style={{ marginLeft: 5, color: 'var(--c-text-3)', fontSize: 11 }}>
                  {isThread ? (threadOpen ? '▴' : '▾') : (expanded ? '▴' : '▾')}
                </span>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', whiteSpace: 'nowrap', marginTop: 1, flexShrink: 0 }}>
            {daysAgo(ev.date) && <span style={{ marginRight: 5 }}>{daysAgo(ev.date)}</span>}
            {formatDateTime(ev.date)}
          </div>
        </div>

        {/* Meta row */}
        {ev.meta && (
          <div style={{ color: 'var(--c-text-3)', fontSize: 12, marginTop: 3 }}>
            {ev.meta}
          </div>
        )}

        {/* Expanded email body */}
        {isEmailType && hasBody && expanded && (
          <EmailBodyPreview body={ev.body} bodyHtml={ev.body_html} bodyFormat={ev.body_format} truncated={ev.body_truncated} />
        )}

        {/* Zoom meeting note expansion */}
        {ev.type === 'zoom_meeting' && expanded && (
          <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--c-bg-2, #f8fafc)', border: '1px solid var(--c-border)', borderRadius: 8, fontSize: 12.5, lineHeight: 1.6 }}>
            {ev.body && <p style={{ color: 'var(--c-text)', marginBottom: ev.zoomItems?.length ? 10 : 0 }}>{ev.body}</p>}
            {ev.zoomItems?.length > 0 && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--c-text-3)', marginBottom: 6 }}>Next Steps</div>
                {ev.zoomItems.map((it, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'flex-start' }}>
                    {it.assignee && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#eff6ff', color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>{it.assignee}</span>}
                    <span style={{ color: 'var(--c-text-2)', fontSize: 12 }}>{it.task || it}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Thread expansion */}
        {isThread && threadOpen && (
          <div style={{ marginTop: 10, borderLeft: '2px solid var(--c-border)', paddingLeft: 12 }}>
            {ev.threadMessages.map((msg, mi) => (
              <ThreadMessage key={mi} msg={msg} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailBodyPreview({ body, bodyHtml, bodyFormat, truncated }) {
  const [showHtml, setShowHtml] = useState(false);
  const hasHtml = !!bodyHtml;

  return (
    <div style={{
      marginTop: 8, padding: '10px 12px',
      background: 'var(--c-bg-2, #f8fafc)', border: '1px solid var(--c-border)',
      borderRadius: 8, fontSize: 12.5, lineHeight: 1.6, color: 'var(--c-text)',
    }}>
      {hasHtml && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <button
            onClick={() => setShowHtml(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: showHtml ? 400 : 700, color: showHtml ? 'var(--c-text-3)' : 'var(--c-primary)' }}
          >Plain text</button>
          <button
            onClick={() => setShowHtml(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: showHtml ? 700 : 400, color: showHtml ? 'var(--c-primary)' : 'var(--c-text-3)' }}
          >Rendered</button>
        </div>
      )}
      {showHtml ? (
        <div
          style={{ maxHeight: 300, overflowY: 'auto', fontSize: 12 }}
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      ) : (
        <pre style={{
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
          fontFamily: 'inherit', maxHeight: 300, overflowY: 'auto',
          color: 'var(--c-text-2)',
        }}>
          {body || '(no plain text content)'}
        </pre>
      )}
      {truncated && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--c-text-3)', fontStyle: 'italic' }}>
          Message truncated — view full thread in Gmail
        </div>
      )}
    </div>
  );
}

function ThreadMessage({ msg }) {
  const [expanded, setExpanded] = useState(false);
  const hasBody = msg.body || msg.body_html;
  const dir = (msg.direction ?? '').toLowerCase();
  const colors = eventColor(msg.type);

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 10, color: colors.badgeText, fontWeight: 700, background: colors.badge, padding: '1px 6px', borderRadius: 4 }}>
            {dir === 'inbound' ? '← Reply' : '→ Sent'}
          </span>
          <span
            style={{ fontSize: 12.5, fontWeight: 500, cursor: hasBody ? 'pointer' : 'default', color: 'var(--c-text)' }}
            onClick={() => hasBody && setExpanded((v) => !v)}
          >
            {msg.title}
            {hasBody && <span style={{ marginLeft: 4, color: 'var(--c-text-3)', fontSize: 11 }}>{expanded ? '▴' : '▾'}</span>}
          </span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--c-text-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>{daysAgo(msg.date)}</span>
      </div>
      {msg.meta && <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 2 }}>{msg.meta}</div>}
      {hasBody && expanded && (
        <EmailBodyPreview body={msg.body} bodyHtml={msg.body_html} bodyFormat={msg.body_format} truncated={msg.body_truncated} />
      )}
    </div>
  );
}
